/*
 * App — the client-side view switcher (hash router) and role-aware chrome.
 *
 * Imports and routes ALL SIX planes so the app compiles today; view engineers
 * overwrite only their own file under src/views/. Role state lives here and is
 * passed to every view (role-aware chrome). The connection indicator goes
 * green ONLY when /healthz returns 200 (useHealthPoll).
 */

import { useState } from "react";
import {
  PlaneNav,
  TopBar,
  useHashRoute,
  useHealthPoll,
} from "./components/AppShell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import type { PlaneId, Role } from "./api/types";

import Overview from "./views/Overview";
import Cockpit from "./views/Cockpit";
import DoneGate from "./views/DoneGate";
import Transport from "./views/Transport";
import Trust from "./views/Trust";
import Catalog from "./views/Catalog";
import Observability from "./views/Observability";
import Consent from "./views/Consent";

const VIEWS: Record<PlaneId, (p: { role: Role }) => JSX.Element> = {
  overview: Overview,
  cockpit: Cockpit,
  donegate: DoneGate,
  transport: Transport,
  trust: Trust,
  catalog: Catalog,
  observability: Observability,
  consent: Consent,
};

export default function App() {
  const [active, navigate] = useHashRoute();
  const [role, setRole] = useState<Role>("operator");
  const health = useHealthPoll(5000);

  const ActiveView = VIEWS[active] ?? Overview;

  return (
    <div className="app">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <TopBar health={health} role={role} onRoleChange={setRole} />
      <div className="app__main">
        <PlaneNav active={active} onNavigate={navigate} />
        <main id="main-content" className="app__content" tabIndex={-1}>
          <ErrorBoundary key={active} surface={active}>
            <ActiveView role={role} />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
